import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, GripVertical, Type, List, Video, Heading } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ContentBlock {
  type: 'paragraph' | 'heading' | 'list' | 'video';
  text: string;
  items?: string[];
  videoId?: string;
}

interface ContentBlockEditorProps {
  blocks: ContentBlock[];
  onChange: (blocks: ContentBlock[]) => void;
}

const blockTypeIcons = {
  paragraph: Type,
  heading: Heading,
  list: List,
  video: Video,
};

const blockTypeLabels = {
  paragraph: 'Paragraphe',
  heading: 'Titre',
  list: 'Liste',
  video: 'Vidéo YouTube',
};

const ContentBlockEditor = ({ blocks, onChange }: ContentBlockEditorProps) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const addBlock = (type: ContentBlock['type']) => {
    const newBlock: ContentBlock = {
      type,
      text: '',
      ...(type === 'list' && { items: [''] }),
      ...(type === 'video' && { videoId: '' }),
    };
    onChange([...blocks, newBlock]);
  };

  const updateBlock = (index: number, updates: Partial<ContentBlock>) => {
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], ...updates };
    onChange(newBlocks);
  };

  const removeBlock = (index: number) => {
    onChange(blocks.filter((_, i) => i !== index));
  };

  const moveBlock = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= blocks.length) return;
    const newBlocks = [...blocks];
    const [movedBlock] = newBlocks.splice(fromIndex, 1);
    newBlocks.splice(toIndex, 0, movedBlock);
    onChange(newBlocks);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      moveBlock(draggedIndex, index);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const updateListItem = (blockIndex: number, itemIndex: number, value: string) => {
    const block = blocks[blockIndex];
    if (block.type !== 'list' || !block.items) return;

    const newItems = [...block.items];
    newItems[itemIndex] = value;
    updateBlock(blockIndex, { items: newItems });
  };

  const addListItem = (blockIndex: number) => {
    const block = blocks[blockIndex];
    if (block.type !== 'list' || !block.items) return;

    updateBlock(blockIndex, { items: [...block.items, ''] });
  };

  const removeListItem = (blockIndex: number, itemIndex: number) => {
    const block = blocks[blockIndex];
    if (block.type !== 'list' || !block.items) return;

    updateBlock(blockIndex, { items: block.items.filter((_, i) => i !== itemIndex) });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Contenu de l'article</Label>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => addBlock('heading')}>
            <Heading className="h-4 w-4 mr-1" />
            Titre
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => addBlock('paragraph')}>
            <Type className="h-4 w-4 mr-1" />
            Paragraphe
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => addBlock('list')}>
            <List className="h-4 w-4 mr-1" />
            Liste
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => addBlock('video')}>
            <Video className="h-4 w-4 mr-1" />
            Vidéo
          </Button>
        </div>
      </div>

      {blocks.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-8 text-center text-muted-foreground">
          <p>Aucun bloc de contenu. Ajoutez-en un avec les boutons ci-dessus.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {blocks.map((block, index) => {
            const Icon = blockTypeIcons[block.type];
            return (
              <Card
                key={index}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={cn(
                  'transition-opacity',
                  draggedIndex === index && 'opacity-50'
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center gap-2 pt-2">
                      <GripVertical className="h-5 w-5 text-muted-foreground cursor-grab" />
                      <Icon className="h-5 w-5 text-muted-foreground" />
                    </div>

                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">
                          {blockTypeLabels[block.type]}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => removeBlock(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      {block.type === 'paragraph' && (
                        <Textarea
                          value={block.text}
                          onChange={(e) => updateBlock(index, { text: e.target.value })}
                          placeholder="Contenu du paragraphe..."
                          rows={4}
                        />
                      )}

                      {block.type === 'heading' && (
                        <Input
                          value={block.text}
                          onChange={(e) => updateBlock(index, { text: e.target.value })}
                          placeholder="Titre de section..."
                        />
                      )}

                      {block.type === 'list' && (
                        <div className="space-y-2">
                          <Input
                            value={block.text}
                            onChange={(e) => updateBlock(index, { text: e.target.value })}
                            placeholder="Titre de la liste (optionnel)..."
                          />
                          <div className="space-y-2 pl-4">
                            {block.items?.map((item, itemIndex) => (
                              <div key={itemIndex} className="flex gap-2">
                                <Input
                                  value={item}
                                  onChange={(e) => updateListItem(index, itemIndex, e.target.value)}
                                  placeholder={`Élément ${itemIndex + 1}...`}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="shrink-0"
                                  onClick={() => removeListItem(index, itemIndex)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addListItem(index)}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Ajouter un élément
                            </Button>
                          </div>
                        </div>
                      )}

                      {block.type === 'video' && (
                        <div className="space-y-2">
                          <Input
                            value={block.text}
                            onChange={(e) => updateBlock(index, { text: e.target.value })}
                            placeholder="Titre de la vidéo (optionnel)..."
                          />
                          <Input
                            value={block.videoId || ''}
                            onChange={(e) => updateBlock(index, { videoId: e.target.value })}
                            placeholder="ID YouTube (ex: dQw4w9WgXcQ)..."
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContentBlockEditor;
